import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Videocam as VideocamIcon,
  School as SchoolIcon,
  Link as LinkIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Camera {
  _id: string;
  name: string;
  cameraId: string;
  kindergarten: {
    _id: string;
    name: string;
  };
  location: string;
  isActive: boolean;
  streamUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface Kindergarten {
  _id: string;
  name: string;
}

interface CameraFormData {
  name: string;
  cameraId: string;
  kindergarten: string;
  location: string;
  isActive: boolean;
  streamUrl: string;
}

const Cameras: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  const [formData, setFormData] = useState<CameraFormData>({
    name: '',
    cameraId: '',
    kindergarten: '',
    location: '',
    isActive: true,
    streamUrl: '',
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [camerasRes, kindergartensRes] = await Promise.all([
        api.get(`/cameras?page=${page}&limit=${itemsPerPage}`),
        api.get('/kindergartens'),
      ]);
      
      setCameras(camerasRes.data.data || []);
      setKindergartens(kindergartensRes.data.data || []);
      
      // Встановлюємо дані пагінації
      if (camerasRes.data.pagination) {
        setTotalPages(camerasRes.data.pagination.totalPages);
        setTotalItems(camerasRes.data.pagination.totalItems);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (camera?: Camera) => {
    if (camera) {
      setEditingCamera(camera);
      setFormData({
        name: camera.name,
        cameraId: camera.cameraId,
        kindergarten: camera.kindergarten._id,
        location: camera.location || '',
        isActive: camera.isActive,
        streamUrl: camera.streamUrl || '',
      });
    } else {
      setEditingCamera(null);
      setFormData({
        name: '',
        cameraId: '',
        kindergarten: '',
        location: '',
        isActive: true,
        streamUrl: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCamera(null);
    setFormErrors({});
    setFormData({
      name: '',
      cameraId: '',
      kindergarten: '',
      location: '',
      isActive: true,
      streamUrl: '',
    });
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Назва камери обов\'язкова';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Назва камери повинна бути від 2 символів';
    }
    
    if (!formData.cameraId.trim()) {
      errors.cameraId = 'ID камери обов\'язковий';
    }
    
    if (!formData.kindergarten) {
      errors.kindergarten = 'Виберіть дитячий садок';
    }
    
    if (!formData.location.trim()) {
      errors.location = 'Розташування камери обов\'язкове';
    } else if (formData.location.trim().length < 2) {
      errors.location = 'Розташування камери повинно бути від 2 символів';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('cameraId', formData.cameraId);
      formDataToSend.append('kindergarten', formData.kindergarten);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('isActive', formData.isActive.toString());
      formDataToSend.append('streamUrl', formData.streamUrl);

      if (editingCamera) {
        await api.put(`/cameras/${editingCamera._id}`, formDataToSend);
      } else {
        await api.post('/cameras', formDataToSend);
      }

      handleCloseDialog();
      fetchData();
      setError(''); // Очищаємо помилки при успіху
    } catch (error: any) {
      console.error('Помилка при збереженні камери:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        setError(`Помилки валідації: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Помилка збереження');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цю камеру?')) {
      try {
        await api.delete(`/cameras/${id}`);
        fetchData();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const handleToggleStatus = async (camera: Camera) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('isActive', (!camera.isActive).toString());
      
      await api.put(`/cameras/${camera._id}`, formDataToSend);
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка оновлення статусу');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Камери
        </Typography>
        {hasPermission('cameras', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати камеру
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Назва камери</TableCell>
                  <TableCell>ID камери</TableCell>
                  <TableCell>Дитячий садок</TableCell>
                  <TableCell>Розташування</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Потік</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cameras.map((camera) => (
                  <TableRow key={camera._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideocamIcon fontSize="small" color="action" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {camera.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={camera.cameraId} 
                        size="small" 
                        variant="outlined"
                        icon={<LinkIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        {camera.kindergarten.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {camera.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {camera.isActive ? (
                          <>
                            <WifiIcon color="success" fontSize="small" />
                            <Chip label="Активна" size="small" color="success" />
                          </>
                        ) : (
                          <>
                            <WifiOffIcon color="error" fontSize="small" />
                            <Chip label="Неактивна" size="small" color="error" />
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {camera.streamUrl ? (
                        <Chip 
                          label="Доступна" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="Не налаштована" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton size="small" color="primary">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('cameras', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(camera)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('cameras', 'update') && (
                          <Tooltip title={camera.isActive ? 'Деактивувати' : 'Активувати'}>
                            <IconButton 
                              size="small" 
                              color={camera.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleStatus(camera)}
                            >
                              {camera.isActive ? <WifiOffIcon /> : <WifiIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('cameras', 'delete') && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(camera._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCamera ? 'Редагувати камеру' : 'Додати камеру'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Назва камери"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                if (formErrors.name) {
                  setFormErrors(prev => ({ ...prev, name: '' }));
                }
              }}
              margin="normal"
              required
              error={!!formErrors.name}
              helperText={formErrors.name || ''}
            />
            <TextField
              fullWidth
              label="ID камери"
              value={formData.cameraId}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, cameraId: e.target.value }));
                if (formErrors.cameraId) {
                  setFormErrors(prev => ({ ...prev, cameraId: '' }));
                }
              }}
              margin="normal"
              required
              error={!!formErrors.cameraId}
              helperText={formErrors.cameraId || 'Унікальний ідентифікатор камери'}
            />
            <FormControl fullWidth margin="normal" required error={!!formErrors.kindergarten}>
              <InputLabel>Дитячий садок</InputLabel>
              <Select
                value={formData.kindergarten}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, kindergarten: e.target.value }));
                  if (formErrors.kindergarten) {
                    setFormErrors(prev => ({ ...prev, kindergarten: '' }));
                  }
                }}
                label="Дитячий садок"
              >
                {kindergartens.map((kindergarten) => (
                  <MenuItem key={kindergarten._id} value={kindergarten._id}>
                    {kindergarten.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.kindergarten && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {formErrors.kindergarten}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Розташування камери"
              value={formData.location}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, location: e.target.value }));
                if (formErrors.location) {
                  setFormErrors(prev => ({ ...prev, location: '' }));
                }
              }}
              margin="normal"
              required
              error={!!formErrors.location}
              helperText={formErrors.location || 'Наприклад: Група А, Ігрова зона, Спальня'}
            />
            <TextField
              fullWidth
              label="URL потоку"
              value={formData.streamUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, streamUrl: e.target.value }))}
              margin="normal"
              helperText="URL для доступу до відеопотоку камери"
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Камера активна"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCamera ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cameras; 